import { WinnerReason } from '@uefadigital/panenka';
import { PkAvatar, PkIcon, PkIdentifier, PkPopover } from '@uefadigital/panenka-react';
import type { ICellRendererParams } from 'ag-grid-community';
import clsx from 'clsx';
import { memo, useCallback, useMemo } from 'react';

import { useWindowSize } from '../../../hooks/useWindowSize';
import { useTranslateFn } from '../../../Translate';

import { hydrateWithTranslations } from '@services/TranslatorService';
import type { Match } from '@shared-types/match.types';
import { MatchWinner } from '@shared-types/match.types';
import type { FormGuide, Standing } from '@shared-types/standing.types';
import { dateToString } from '@utilities/dateTime';
import { matchScore, matchWinner, matchResult } from '@utilities/live';

import * as classes from './styles.module.scss';

type FGuideAvatar = {
  bgColor: string;
  label: string;
  size?: number;
  theme: string;
  underline?: boolean;
};

const FormGuideAvatar = memo(({ bgColor, label, size = 24, theme, underline = false }: FGuideAvatar) => (
  <PkAvatar
    backgroundColor={bgColor}
    className={clsx(underline && classes.avatarUnderline)}
    pk-theme={theme}
    size={size}
    slot='prefix'
    type='text'
  >
    <span className='pk-text--text-01 pk-font-size--xs2' slot='text'>
      {label}
    </span>
  </PkAvatar>
));
FormGuideAvatar.displayName = 'FormGuideAvatar';

const FormGuideIdentifier = memo(({ bgColor, label, match: m, theme }: FGuideAvatar & { match: Match }) => (
  <PkIdentifier padY='0'>
    <FormGuideAvatar bgColor={bgColor} label={label} theme={theme} />
    <span slot='primary'>
      {hydrateWithTranslations(m.homeTeam).displayName} {matchScore(m, 'home')} - {matchScore(m, 'away')}{' '}
      {hydrateWithTranslations(m.awayTeam).displayName}
    </span>
    <span slot='secondary'>
      {dateToString(m.kickOffTime.date, 'D MMM YYYY')} - {hydrateWithTranslations(m.competition).name}
    </span>
  </PkIdentifier>
));
FormGuideIdentifier.displayName = 'FormGuideIdentifier';

const FormGuideCellRender = ({
  disablePopover,
  value,
}: ICellRendererParams<Standing, FormGuide> & {
  disablePopover: boolean;
}) => {
  const { matches, teamId } = value;

  const t = useTranslateFn();
  const { isMobile } = useWindowSize();

  const filteredMatches = useMemo(
    () => matches?.filter((m) => matchWinner(m)?.reason === WinnerReason.DRAW || matchWinner(m)?.team),
    [matches],
  );

  const numberOfMatches = useMemo(() => (isMobile ? 3 : 5), [isMobile]);
  const matchesPlaceholders = useMemo(
    () =>
      Array.from({ length: filteredMatches ? numberOfMatches - filteredMatches.length : numberOfMatches }, (_, i) => i),
    [filteredMatches, numberOfMatches],
  );

  const getBgColor = useCallback(
    (match: Match) => {
      const result = matchResult(match, teamId);
      if (result === 'draw') return 'ui-03';
      if (result === 'won') return 'support-01';

      return 'support-04';
    },
    [teamId],
  );

  const getTheme = useCallback(
    (match: Match) => (matchResult(match, teamId).toUpperCase() === MatchWinner.DRAW ? 'light' : 'dark'),
    [teamId],
  );

  const getLabel = useCallback((match: Match) => t(`${matchResult(match, teamId)}_abbr`), [teamId, t]);

  const formGuide = useMemo(
    () => (
      <div className={clsx(classes.formguide, 'pk-d--flex pk-flex--row-reverse pk-flex--wrap')} slot='trigger'>
        {!disablePopover && !!filteredMatches?.length && <PkIcon name='navigation-expand' color='ui-03' size={18} />}
        {filteredMatches
          ?.slice(0, numberOfMatches)
          .map((m: Match, i: number) => (
            <FormGuideAvatar
              key={m.id}
              bgColor={getBgColor(m)}
              label={getLabel(m)}
              theme={getTheme(m)}
              size={18}
              underline={i === 0}
            />
          ))}
        {matchesPlaceholders.map((i) => (
          <div key={`placeholder-${i}`} className={classes.placeholder} />
        ))}
      </div>
    ),
    [disablePopover, getBgColor, getLabel, getTheme, filteredMatches, matchesPlaceholders, numberOfMatches],
  );

  return (
    <>
      {disablePopover || !filteredMatches?.length ? (
        formGuide
      ) : (
        <PkPopover className={classes.popover} placement='bottom-end' size='small'>
          {formGuide}
          <h1 slot='header'>
            {t('last_n_matches', { replace: `${Math.min(numberOfMatches, filteredMatches.length)}` })}
          </h1>
          <div className='pk-d--flex pk-flex--column pk-gap--xs'>
            {filteredMatches
              ?.slice(0, numberOfMatches)
              .map((m: Match) => (
                <FormGuideIdentifier
                  key={`${teamId}-${m.id}`}
                  bgColor={getBgColor(m)}
                  label={getLabel(m)}
                  match={m}
                  theme={getTheme(m)}
                />
              ))}
          </div>
        </PkPopover>
      )}
    </>
  );
};

export default memo(FormGuideCellRender);
